import { GameSettings, defaultSettings } from '../Models/Settings'

const SETTINGS_KEY = 'solitaire-settings'

/**
 * Validate that a parsed object has the expected shape for GameSettings.
 * Returns a sanitised settings object merged with defaults, or null if
 * the input is clearly not a settings object (e.g. wrong type at the root).
 */
function validateSettings(parsed: unknown): GameSettings | null {
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null

    const obj = parsed as Record<string, unknown>

    // Sanitise individual fields — fall back to defaults for invalid values
    const drawCount = obj.drawCount === 1 || obj.drawCount === 3 ? obj.drawCount : defaultSettings.drawCount
    const handedness =
        obj.handedness === 'left' || obj.handedness === 'right' ? obj.handedness : defaultSettings.handedness
    const cardBackDesign =
        obj.cardBackDesign === 'pattern' || obj.cardBackDesign === 'solid'
            ? obj.cardBackDesign
            : defaultSettings.cardBackDesign
    const themeName = typeof obj.themeName === 'string' ? obj.themeName : defaultSettings.themeName

    return {
        ...defaultSettings,
        ...(parsed as Partial<GameSettings>),
        // Overwrite with explicitly validated values
        drawCount,
        handedness,
        cardBackDesign,
        themeName,
        // Booleans — coerce to boolean, defaulting if missing/wrong type
        autoFlipCards:
            typeof obj.autoFlipCards === 'boolean' ? obj.autoFlipCards : defaultSettings.autoFlipCards,
        hapticsEnabled:
            typeof obj.hapticsEnabled === 'boolean' ? obj.hapticsEnabled : defaultSettings.hapticsEnabled,
        soundEnabled: typeof obj.soundEnabled === 'boolean' ? obj.soundEnabled : defaultSettings.soundEnabled,
        animationsEnabled:
            typeof obj.animationsEnabled === 'boolean'
                ? obj.animationsEnabled
                : defaultSettings.animationsEnabled,
    }
}

export function saveSettings(settings: GameSettings): void {
    try {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
    } catch (error) {
        console.error('Failed to save settings:', error)
    }
}

export function loadSettings(): GameSettings {
    try {
        const stored = localStorage.getItem(SETTINGS_KEY)
        if (stored) {
            const parsed = JSON.parse(stored)
            const validated = validateSettings(parsed)
            if (validated) return validated
            console.warn('Stored settings failed validation, using defaults')
        }
    } catch (error) {
        console.warn('Failed to load settings, using defaults:', error)
    }
    return { ...defaultSettings }
}
